import { Row, Col, Typography, Button, Tooltip, Alert, Select, Tabs } from "antd";
import { DataSourceType, IConfigureMetric, IGpsIntegration, IGpsMetricMappingSample, IGpsMetricMappingValidation, ISampleDataPointDataType } from "./GpsMetricMapping/consts";
import { useEffect, useState } from "react";
import SampleDatapoint from "./GpsMetricMapping/SampleDatapoint";
import { DataSource } from "./GpsMetricMapping/DataSource";
import { trainingAPI } from "@api/TrainingAPI";
import ConfigureMetrics from "./GpsMetricMapping/ConfigureMetrics";
import { audioMessage } from "@components/Common/Notifications/audioMessage";
import { useDispatch, useSelector } from "react-redux";
import { getAllGpsMetrics, getAllTeamMetricsByType } from "@store/actions/team.actions";
import { AdminNotice } from "./AdminNotice";
import { SessionDurationSource } from "./GpsMetricMapping/SessionDurationSource";

const { Text } = Typography;

// The mapping endpoints answer with either a plain string or
// { message, errors: [{ error }] }. String() on the object shape prints
// "[object Object]", so the reader never sees the reason.
const describeApiError = (err: any, fallback: string): string => {
    const data = err?.response?.data
    if (data === undefined || data === null) return err?.message || fallback
    if (typeof data === 'string') return data

    const details: string = Array.isArray(data.errors)
        ? data.errors.map((e: any) => e?.error).filter(Boolean).join('; ')
        : ''
    return [data.message, details].filter(Boolean).join(': ') || fallback
}

// A row that the user never mapped. The team metric list supplies these, and
// the backend ignores them. Do not send them.
const isConfiguredMetric = (m: IConfigureMetric): boolean => (
    Boolean(m.extractionApproach) && Boolean(m.extractionText?.toString().trim())
)

export const GpsMetricMapping = () => {
    const dispatch = useDispatch();

    // /api/gps-metrics is staff-gated (401 for everyone else), so it is fetched
    // on this page rather than preloaded with the rest of the team data.
    useEffect(() => {
        dispatch(getAllGpsMetrics()).catch(() =>
            audioMessage.error("Couldn't load the GPS metric list.")
        );
    }, []);

    let {
        metrics,
        info: {
            gps_mapping_strategy_version,
            date_format,
            integrations
        },
        misc: {
            allowed_csv_date_format_types: DATE_FORMAT_OPTIONS
        }
    } = useSelector(({ team }) => team);

    const gpsIntegrations: IGpsIntegration[] = integrations?.gps ?? []
    const isMultiUnit = gpsIntegrations.length > 1

    const [page, setPage] = useState<number>(0);
    const [activeUnitKey, setActiveUnitKey] = useState<string>(() => gpsIntegrations[0]?.key ?? '')
    const [dataSourceByUnit, setDataSourceByUnit] = useState<Record<string, DataSourceType>>({})
    const [originalConfigureMetricsData, setOriginalConfigureMetricsData] = useState<IConfigureMetric[]>([])
    const [configureMetricsData, setConfigureMetricsData] = useState<IConfigureMetric[]>([])
    const [sampleDataByUnit, setSampleDataByUnit] = useState<Record<string, IGpsMetricMappingSample>>({})
    const [validationDataByUnit, setValidationDataByUnit] = useState<Record<string, IGpsMetricMappingValidation>>({})
    const [metricIdsToDelete, setMetricIdsToDelete] = useState<number[]>([]);
    const [CSVDateFormatByUnit, setCSVDateFormatByUnit] = useState<Record<string, string | undefined>>({})

    const dataSourceSubmit = async (data: object, unitKey: string) => {
        const mode = dataSourceByUnit[unitKey] ?? 'api'
        let gpsMetricMapping;
        try {
            gpsMetricMapping = await trainingAPI.postGpsMetricMappingDataSource(mode, { ...data, source_key: unitKey })
        } catch (err: any) {
            const reason = describeApiError(err, "No data found")
            console.error(reason)
            audioMessage.error(reason)
            return
        }
        setSampleDataByUnit(prev => ({ ...prev, [unitKey]: gpsMetricMapping }))
    }

    const saveMetrics = async (unitKey: string) => {
        const unitMetrics = configureMetricsData.filter(m => m.source_gps_unit === unitKey)
        const hasMetrics = unitMetrics.length > 0

        if (hasMetrics && !validationDataByUnit[unitKey]?.all_valid) {
            audioMessage.warning(`Please validate before saving`)
            return
        }

        try {
            const originalUnitMetricIds = new Set(
                originalConfigureMetricsData
                    .filter(m => m.source_gps_unit === unitKey)
                    .map(m => Number(m.metric))
            )
            const unitIdsToDelete = metricIdsToDelete.filter(id => originalUnitMetricIds.has(id))

            if (unitIdsToDelete.length > 0) {
                await trainingAPI.deleteGpsMetricMapping(unitIdsToDelete)
                setMetricIdsToDelete(prev => prev.filter(id => !originalUnitMetricIds.has(id)))
            }

            // Send active unit's current state + all other units' original state.
            // The backend replaces all mappings on POST, so omitting other units
            // would delete their source_gps_unit assignments.
            const otherUnitsMetrics = originalConfigureMetricsData
                .filter(m => m.source_gps_unit !== unitKey)
                .filter(m => !metricIdsToDelete.includes(Number(m.metric)))

            const allMetricsToSend = [...unitMetrics, ...otherUnitsMetrics]
                .filter(isConfiguredMetric)

            if (allMetricsToSend.length > 0) {
                await trainingAPI.postGpsMetricMapping(
                    allMetricsToSend.map((e) => ({
                        "metric_id": e.metric,
                        "mapping_mode": e.extractionApproach,
                        "mapping": e.extractionText,
                        "conversion": e.conversion || '',
                        "unit": e.units || '',
                        "source_gps_unit": e.source_gps_unit || ''
                    })),
                    CSVDateFormatByUnit[unitKey],
                    dataSourceByUnit[unitKey] ?? 'api'
                )
            }

            // Refresh both team.metrics and team.all_metric_types so downstream
            // pages (e.g. Scatterplot, which reads all_metric_types.gps) reflect
            // deletions/edits without a full page reload.
            await dispatch(getAllTeamMetricsByType())
            audioMessage.success(`Saved successfully`)
        } catch (err: any) {
            const reason = describeApiError(err, `Error saving`)
            console.error(reason)
            audioMessage.error(reason)
        }
    }

    // Deps matter: on a direct URL load team.info hydrates after mount, so this
    // must re-run when integrations/date_format arrive. Guards below keep the
    // re-run from clobbering a unit tab or date format the user already picked.
    useEffect(() => {
        const defaultUnitKey = gpsIntegrations[0]?.key ?? ''

        if (defaultUnitKey) {
            setActiveUnitKey(prev => prev || defaultUnitKey)
        }

        if (defaultUnitKey && date_format) {
            setCSVDateFormatByUnit(prev => (
                prev[defaultUnitKey] ? prev : { ...prev, [defaultUnitKey]: date_format }
            ))
        }

        ;(async () => {
            const metricsLocal = await trainingAPI.getTeamMetrics(undefined, undefined, undefined, undefined, undefined, true)

            const metricsDataFormattedLocal = metricsLocal
                .filter((e: any) => !e.is_user_defined)
                .map((e: any) => ({
                    metric: e.metric_id,
                    extractionApproach: e.mapping_info?.mapping_mode,
                    extractionText: e.mapping_info?.mapping,
                    conversion: e.mapping_info?.conversion,
                    units: e.mapping_info?.unit,
                    source_gps_unit: e.mapping_info?.source_gps_unit ?? defaultUnitKey,
                }))
            setConfigureMetricsData(metricsDataFormattedLocal)
            setOriginalConfigureMetricsData(metricsDataFormattedLocal)
        })()
    }, [integrations, date_format])

    const getMetricMappingSampleAsFieldValue = (unitKey: string): ISampleDataPointDataType[][] => {
        return sampleDataByUnit[unitKey]?.rows.map((row) => {
            return Object.entries(row)
                .map(([key, value]) => ({
                    field: key,
                    value: value || ""
                }))
                .sort((a, b) => a.field.localeCompare(b.field))
        }) || []
    }

    const filteredMetricCounts = gpsIntegrations.reduce((acc, integ) => {
        acc[integ.key] = configureMetricsData.filter(m => m.source_gps_unit === integ.key).length
        return acc
    }, {} as Record<string, number>)

    const renderTabContent = (unitKey: string) => {
        const uDataSource = dataSourceByUnit[unitKey] ?? 'api'
        const uSampleData = sampleDataByUnit[unitKey]
        const uCSVDateFormat = CSVDateFormatByUnit[unitKey]

        const uMetricsWithIndex = configureMetricsData
            .map((m, i) => ({ m, i }))
            .filter(({ m }) => m.source_gps_unit === unitKey)
        const uGlobalIndexes = uMetricsWithIndex.map(({ i }) => i)
        const uFilteredMetrics = uMetricsWithIndex.map(({ m }) => m)
        const uValidationData = validationDataByUnit[unitKey]
        const isUnitValidated = !configureMetricsData.some(m => m.source_gps_unit === unitKey)
            || validationDataByUnit[unitKey]?.all_valid

        const handleValidate = async () => {
            let dateFormatIp: string[] = [];
            if (uDataSource === "csv") {
                if (!uCSVDateFormat) {
                    audioMessage.warning(`Please select a valid CSV Date Format to proceed with validation.`);
                    return
                }

                dateFormatIp.push(uCSVDateFormat);

                if (!uSampleData || uSampleData.rows.length <= 0 || uSampleData.headers.length <= 0) {
                    audioMessage.error(`Invalid sample datapoint to proceed with validation.`);
                    return
                }

                const dateColKeys = ['date', 'Date', 'Start time', 'Start Time', 'Days', 'Date - Session Date'];
                const dateColIndex = uSampleData.headers.findIndex((h) => dateColKeys.includes(h));
                if (dateColIndex < 0) {
                    audioMessage.error(`Couldn't find date column to proceed with date format validation.`);
                    return
                }
                const row = uSampleData.rows?.[0] as any[];
                if (row && dateColIndex > -1) {
                    dateFormatIp.push(Object.values(row)[dateColIndex]);
                }

                if (dateFormatIp.length < 2) {
                    audioMessage.error(`Couldn't perform date format validation.`);
                    return
                }
            }

            const metricsMappings = uFilteredMetrics.map((e) => ({
                "metric_id": e.metric,
                "mapping_mode": e.extractionApproach,
                "mapping": e.extractionText,
                "conversion": e.conversion || '',
                "unit": e.units || '',
                "source_gps_unit": e.source_gps_unit || ''
            }))

            try {
                const data: IGpsMetricMappingValidation = await trainingAPI.postValidateGpsMetricsMapping(
                    metricsMappings,
                    dateFormatIp,
                    uDataSource
                )

                if (data.all_valid) {
                    audioMessage.success(`Validation successful`);
                }

                setValidationDataByUnit(prev => ({ ...prev, [unitKey]: data }))
            } catch (error: any) {
                audioMessage.error(describeApiError(error, 'Validation Failed'));
            }
        }

        return (
            <>
                <div style={{ marginTop: '20px' }}>
                    <DataSource
                        unitKey={unitKey}
                        integrationName={gpsIntegrations.find(i => i.key === unitKey)?.name ?? ''}
                        dataSource={uDataSource}
                        setDataSource={(ds) => setDataSourceByUnit(prev => ({ ...prev, [unitKey]: ds }))}
                        dataSourceSubmit={(data) => dataSourceSubmit(data, unitKey)}
                    />
                </div>

                <hr />

                {uDataSource === "csv" && (
                    <div>
                        <Text strong style={{ marginRight: '10px' }}>Please select a date format. All uploaded CSV dates must follow this format.</Text>
                        <Select
                            optionFilterProp="value"
                            allowClear
                            placeholder="CSV Date Format"
                            onChange={(selectedDateFormat) => setCSVDateFormatByUnit(prev => ({ ...prev, [unitKey]: selectedDateFormat }))}
                            style={{ width: 400, marginLeft: "0.5em" }}
                            defaultValue={uCSVDateFormat}
                            options={DATE_FORMAT_OPTIONS}
                        />
                    </div>
                )}

                <div style={{ marginTop: '20px' }}>
                    <Row justify={'space-between'}>
                        <Col span={12} style={{ paddingRight: '10px' }}>
                            <ConfigureMetrics
                                originalConfigureMetricsData={originalConfigureMetricsData}
                                configureMetricsData={configureMetricsData}
                                filteredConfigureMetricsData={uFilteredMetrics}
                                globalIndexesForActiveTab={uGlobalIndexes}
                                activeUnitKey={unitKey}
                                setConfigureMetricsData={setConfigureMetricsData}
                                metricMappingSampleDatapoints={getMetricMappingSampleAsFieldValue(unitKey)}
                                metricIdsToDelete={metricIdsToDelete}
                                setMetricIdsToDelete={setMetricIdsToDelete}
                                validationData={uValidationData}
                                page={page}
                            />
                        </Col>

                        <Col span={12} style={{ paddingLeft: '10px', overflow: 'visible' }}>
                            {uSampleData && (
                                <div style={{ position: 'sticky', top: '70px' }}>
                                    <SampleDatapoint
                                        integrationName={gpsIntegrations.find(i => i.key === unitKey)?.name ?? ''}
                                        metricMappingSampleDatapoints={getMetricMappingSampleAsFieldValue(unitKey)}
                                        page={page}
                                        setPage={setPage}
                                    />
                                </div>
                            )}
                        </Col>
                    </Row>

                    <Row
                        justify={'end'}
                        style={{ marginTop: '20px' }}
                    >
                        <Button
                            onClick={handleValidate}
                            style={{ marginRight: '10px' }}
                        >
                            Validate
                        </Button>

                        <Tooltip title={!isUnitValidated && 'Need to validate first'}>
                            <Button
                                type="primary"
                                onClick={() => saveMetrics(unitKey)}
                                disabled={!isUnitValidated}
                            >
                                Save Metrics
                            </Button>
                        </Tooltip>
                    </Row>
                </div>
            </>
        )
    }

    return (
        <>
            <AdminNotice description={undefined} />
            <Alert
                message={`Team is currently using metric mapping v${gps_mapping_strategy_version}.`}
                type="info"
                showIcon
                style={{ marginBottom: '20px', marginTop: '10px' }}
            />

            <Text type="secondary">Paste your GPS api key or CSV file to start mapping your data in Whistle Performance.</Text>

            <SessionDurationSource />

            {isMultiUnit ? (
                <Tabs
                    activeKey={activeUnitKey}
                    onChange={setActiveUnitKey}
                    style={{ marginTop: '10px' }}
                    items={gpsIntegrations.map(integ => ({
                        key: integ.key,
                        label: `${integ.name} (${filteredMetricCounts[integ.key] ?? 0})`,
                        children: renderTabContent(integ.key)
                    }))}
                />
            ) : (
                activeUnitKey ? renderTabContent(activeUnitKey) : null
            )}
        </>
    )
}

export default GpsMetricMapping
